import AnimatedHeading from "../../animations/AnimatedHeading";

import { Cog, Cpu, Headphones, ThumbsUp } from "lucide-react";

import GlareHover from "../../animations/GlareHover";

import StarBorder from "../../animations/StarBorder";

import TiltedCard from "../../animations/TiltedCard";

import SpotlightCard from "../../animations/SpotlightCard";

import WavyText from "../../animations/WavyText";

import { useState } from "react";

const cards = [
  {
    icon: ThumbsUp,
    title: "Proven Track Record",
    desc: "With a portfolio of successful projects and satisfied clients, we have a reputation for excellence.",
  },
  {
    icon: Cog,
    title: "Tailored Solutions",
    desc: "Our services are customized to meet your unique business needs, ensuring a perfect fit every time.",
  },
  {
    icon: Cpu,
    title: "Future Technologies",
    desc: "Stay ahead of the competition with AI, cloud computing, and advanced automation solutions.",
  },
  {
    icon: Headphones,
    title: "24/7 Support",
    desc: "Our dedicated support team is available around the clock to ensure your business never stops.",
  },
];

export default function WhyChooseUs() {
  const [hovered, setHovered] = useState(null);

  return (
    <section
      className="
        w-full
        overflow-hidden
        bg-[var(--bg-secondary)]
        px-4
        py-12
        min-[375px]:px-5
        min-[375px]:py-14
        sm:px-6
        sm:py-16
        md:px-8
        md:py-16
        lg:px-14
        lg:py-16
      "
    >
      <div className="mx-auto w-full max-w-7xl">
        {/* =====================================================
            TOP ROW
        ===================================================== */}

        <div
          className="
            flex
            w-full
            justify-center
            px-0
            text-center
            min-[375px]:px-1
            sm:px-2
            md:px-4
          "
        >
          <div
            className="
              w-full
              max-w-5xl
            "
          >
            {/* =================================================
                SECTION LABEL
            ================================================= */}

            <p
              className="
                mb-4
                text-[10px]
                font-bold
                uppercase
                tracking-[0.2em]
                text-[var(--brand-primary)]
                min-[360px]:tracking-[0.22em]
                min-[375px]:mb-5
                min-[375px]:text-[11px]
                min-[375px]:tracking-[0.25em]
                sm:mb-6
                sm:text-xs
                sm:tracking-[0.3em]
                md:text-sm
              "
            >
              [ Why Choose Us ]
            </p>

            {/* =================================================
                HEADING
            ================================================= */}

            <AnimatedHeading
              as="h2"
              parts={["Reliable IT Solutions, for", "Best Results."]}
              className="
                mx-auto
                w-full
                font-saira
                text-2xl
                font-semibold
                leading-[1.15]
                text-[var(--text-primary)]
                min-[360px]:text-[26px]
                min-[375px]:text-[28px]
                sm:max-w-4xl
                sm:text-3xl
                sm:leading-tight
                md:text-5xl
                lg:text-6xl
              "
            />
          </div>
        </div>

        {/* =====================================================
            FEATURE CARDS
        ===================================================== */}

        <div
          className="
            mt-9
            grid
            w-full
            grid-cols-1
            gap-4
            min-[375px]:mt-10
            min-[375px]:gap-5
            sm:mt-12
            sm:grid-cols-2
            sm:gap-5
            md:gap-6
            lg:mt-12
            lg:grid-cols-4
            lg:gap-8
          "
        >
          {cards.map(({ icon: Icon, title, desc }, index) => (
            <div
              key={title}
              className="
                group
                flex
                h-full
                min-w-0
                w-full
              "
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
            >
              <TiltedCard
                containerHeight="100%"
                containerWidth="100%"
                scaleOnHover={1.02}
                rotateAmplitude={5}
                edgeSoftness={0.7}
                deadZone={0.12}
                activationThreshold={4}
              >
                <StarBorder
                  className="
                    h-full
                    w-full
                    overflow-hidden
                    rounded-2xl
                  "
                  color="var(--brand-primary)"
                  speed="6s"
                  active={hovered === index}
                >
                  <GlareHover
                    width="100%"
                    height="100%"
                    background="transparent"
                    borderRadius="16px"
                    borderColor="transparent"
                    glareColor="var(--brand-primary)"
                    glareOpacity={0.55}
                    glareAngle={-35}
                    glareSize={300}
                    transitionDuration={650}
                    className="
                      h-full
                      w-full
                      rounded-2xl
                    "
                  >
                    <SpotlightCard
                      className="
                        flex
                        h-full
                        min-h-[220px]
                        w-full
                        rounded-2xl
                        border
                        border-[var(--border-primary)]
                        bg-[var(--card-bg-secondary)]
                        p-5
                        shadow-[0_25px_50px_-12px_var(--shadow-color)]
                        transition-all
                        duration-500
                        hover:bg-[var(--bg-tertiary)]
                        min-[360px]:min-h-[225px]
                        min-[375px]:min-h-[240px]
                        min-[375px]:p-6
                        sm:min-h-[245px]
                        sm:p-7
                        md:min-h-[255px]
                        md:p-8
                        lg:min-h-[280px]
                        lg:p-8
                      "
                      tailwindColor="var(--brand-primary)"
                      alpha={0.3}
                      radius={500}
                    >
                      <div
                        className="
                          flex
                          h-full
                          w-full
                          flex-col
                          items-start
                          gap-4
                          min-[375px]:gap-5
                          sm:gap-5
                          md:gap-6
                        "
                      >
                        

                        <div
                          className="
                            flex
                            h-12
                            w-12
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-[var(--bg-primary)]
                            text-[var(--brand-primary)]
                            ring-1
                            ring-[var(--border-primary)]
                            transition-all
                            duration-300
                            group-hover:ring-[var(--brand-primary)]
                            min-[375px]:h-[52px]
                            min-[375px]:w-[52px]
                            sm:h-14
                            sm:w-14
                          "
                        >
                          <Icon
                            className="
                              h-6
                              w-6
                              min-[375px]:h-[25px]
                              min-[375px]:w-[25px]
                              sm:h-7
                              sm:w-7
                            "
                          />
                        </div>

                      

                        <div className="min-w-0 w-full">
                          <h3
                            className="
                              break-words
                              text-[17px]
                              font-bold
                              leading-tight
                              tracking-tight
                              text-[var(--text-primary)]
                              min-[360px]:text-[18px]
                              min-[375px]:text-xl
                              sm:text-xl
                            "
                          >
                            <WavyText text={title} />
                          </h3>

                          <p
                            className="
                              mt-3
                              break-words
                              text-[13px]
                              leading-6
                              text-[var(--text-muted)]
                              transition-colors
                              duration-300
                              group-hover:text-[var(--text-secondary)]
                              min-[360px]:text-[13.5px]
                              min-[375px]:mt-3.5
                              min-[375px]:text-sm
                              min-[375px]:leading-6
                              sm:mt-4
                              sm:text-sm
                              sm:leading-relaxed
                            "
                          >
                            {desc}
                          </p>
                        </div>
                      </div>
                    </SpotlightCard>
                  </GlareHover>
                </StarBorder>
              </TiltedCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}